import React, {useState} from "react";
import {Autocomplete, Box, Collapse, IconButton, InputBase, MenuItem, Select, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowDown01Icon, Mic01Icon, VolumeHighIcon} from "@hugeicons/core-free-icons";
import {
  controlSx, FieldHelper, FieldLabel, FlatAutocompleteInput, selectSx,
} from "../../../components/modals/formFieldShared";
import {AUDIO_ENCODINGS, GEMINI_VOICE_NAMES} from "./voiceOptions";

// Default voice / transcribe configuration for a newly selected language. Language codes default to the
// language's code, and a language with exactly one transcribe model gets it preselected.
export const createLanguageConfig = (language, transcribeModels) => {
  // language: a catalog language, or a sub project item whose languageId differs from its id
  const models = (transcribeModels || []).filter(model => model.languageId === (language?.languageId ?? language?.id));
  return {
    voiceConfiguration: {
      voiceModelId: null,
      voice: "",
      languageCode: language?.code || "",
      audioEncoding: "LINEAR16",
      pitch: 0,
      speakingRate: 1,
    },
    transcribeConfiguration: {
      transcribeModelId: models.length === 1 ? models[0].id : null,
      languageCode: language?.code || "",
    },
    // delivery guidance for the voice, stored only in the Firebase flow
    prompt: "",
  };
};

// Voice / transcribe configuration of one language as sent to the API
export const toApiConfiguration = (config) => ({
  voiceConfiguration: {
    voiceModelId: config.voiceConfiguration.voiceModelId || null,
    voice: config.voiceConfiguration.voice || "",
    languageCode: config.voiceConfiguration.languageCode || "",
    audioEncoding: config.voiceConfiguration.audioEncoding || "LINEAR16",
    pitch: config.voiceConfiguration.pitch ?? 0,
    speakingRate: config.voiceConfiguration.speakingRate ?? 1,
  },
  transcribeConfiguration: {
    transcribeModelId: config.transcribeConfiguration.transcribeModelId || null,
    languageCode: config.transcribeConfiguration.languageCode || "",
  },
});

// The flow runtime reads the voice from the Firebase settings, in the Gemini TTS shape
export const toVoiceGemini = (config, voiceModels) => {
  const voice = config?.voiceConfiguration;
  if (!voice?.voiceModelId) return null;
  const voiceModel = voiceModels.find(model => model.id === voice.voiceModelId);
  return {
    voice: {modelName: voiceModel?.modelId || null, name: voice.voice || "", languageCode: voice.languageCode || ""},
    audioConfig: {audioEncoding: voice.audioEncoding || "LINEAR16", pitch: voice.pitch ?? 0, speakingRate: voice.speakingRate ?? 1},
    input: {prompt: config.prompt || ""},
  };
};

export const SubHeader = ({icon, title}) => (
  <Stack direction={"row"} gap={1} alignItems={"center"} sx={{mb: 1.5, color: "#8A8AA0"}}>
    <HugeiconsIcon icon={icon} size={15}/>
    <Typography sx={{fontSize: 12, fontWeight: 700, color: "#6B6B80", textTransform: "uppercase", letterSpacing: "0.05em"}}>{title}</Typography>
  </Stack>
);

// Small circular chevron that springs 180deg open - matches the expand/collapse pattern used across
// registry-project (see RegistryOrganizationsPage.js's ExpandToggle)
const ExpandToggle = ({expanded}) => (
  <Box sx={{
    width: 26, height: 26, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
    color: expanded ? "#4B55C9" : "#8A8AA0", bgcolor: expanded ? "rgba(132, 145, 247, 0.12)" : "#FAFAFD",
    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease",
  }}>
    <HugeiconsIcon icon={ArrowDown01Icon} size={16}/>
  </Box>
);

const FlatField = ({label, value, onChange, helper, placeholder, type, inputProps, multiline, minRows}) => (
  <Box>
    <FieldLabel>{label}</FieldLabel>
    <Box sx={{...controlSx, ...(multiline && {height: "auto", alignItems: "flex-start", py: 1})}}>
      <InputBase
        type={type} placeholder={placeholder} value={value} onChange={onChange} multiline={multiline} minRows={minRows}
        inputProps={{"aria-label": label, ...inputProps}} sx={{flex: 1, fontSize: 13, color: "#23264D"}}
      />
    </Box>
    {helper && <FieldHelper>{helper}</FieldHelper>}
  </Box>
);

// One collapsible voice + transcribe configuration per selected language.
// renderExtra / renderAfter(languageId, config) add fields above the voice configuration and below the
// transcribe configuration (status and core configuration in project settings).
export const LanguageConfigurationsNew = ({
  languageIds, languages, configs, voiceModels, transcribeModels, onChange, renderExtra, renderAfter,
}) => {
  const [openId, setOpenId] = useState(languageIds?.[0] ?? null);
  if (!languageIds?.length) return null;

  return (
    <Stack direction={"column"} gap={1.25} sx={{mt: 2}}>
      {languageIds.map((languageId) => {
        const language = languages.find(item => item.id === languageId);
        const config = configs?.[languageId];
        if (!config) return null;

        const {voiceConfiguration: voice, transcribeConfiguration: transcribe} = config;
        // keyed by sub project id in project settings, so the transcribe models follow the item's own language
        const languageModels = (transcribeModels || []).filter(model => model.languageId === (language?.languageId ?? languageId));
        const voiceModelName = voiceModels?.find(model => model.id === voice.voiceModelId)?.name;
        const transcribeModelName = languageModels.find(model => model.id === transcribe.transcribeModelId)?.name;
        const expanded = openId === languageId;

        const update = (section, key, value) => onChange(languageId, {
          ...config,
          [section]: {...config[section], [key]: value},
        });
        const toNumber = (value) => value === "" ? null : Number(value);

        return (
          <Box key={languageId} sx={{bgcolor: "#fff", border: "1px solid #ECECF3", borderRadius: "14px", overflow: "hidden"}}>
            <Stack
              direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={1.5}
              onClick={() => setOpenId(expanded ? null : languageId)}
              sx={{px: 2, py: 1.5, cursor: "pointer", "&:hover": {bgcolor: "#FAFAFD"}}}
            >
              <Stack direction={{xs: "column", sm: "row"}} gap={{xs: 0.5, sm: 1.5}} alignItems={{sm: "center"}} sx={{minWidth: 0}}>
                <Stack direction={"row"} gap={1} alignItems={"center"}>
                  <Typography sx={{fontSize: 14, fontWeight: 700, color: "#23264D"}}>{language?.language || "Language"}</Typography>
                  {language?.code && (
                    <Box component={"span"} sx={{px: 0.875, py: 0.125, borderRadius: "6px", fontSize: 11, fontWeight: 600, color: "#6B6B80", bgcolor: "#F2F2F7"}}>
                      {language.code}
                    </Box>
                  )}
                </Stack>
                <Typography noWrap sx={{fontSize: 12.5, color: "#8A8AA0"}}>
                  Voice: {voiceModelName || "Default"} · Transcribe: {transcribeModelName || "Default"}
                </Typography>
              </Stack>
              <IconButton size={"small"} disableRipple sx={{p: 0}}>
                <ExpandToggle expanded={expanded}/>
              </IconButton>
            </Stack>

            <Collapse in={expanded} timeout={"auto"} unmountOnExit>
              <Box sx={{px: 2, pb: 2.5}}>
                {renderExtra && <Box sx={{mb: 3}}>{renderExtra(languageId, config)}</Box>}
                <Box sx={{mb: 3}}>
                  <SubHeader icon={VolumeHighIcon} title={"Voice Configuration"}/>
                  <Stack gap={1.5}>
                    <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
                      <Box sx={{flex: 1}}>
                        <FieldLabel>Voice Model</FieldLabel>
                        <Select
                          value={voice.voiceModelId ?? ""} displayEmpty sx={selectSx}
                          onChange={e => update("voiceConfiguration", "voiceModelId", e.target.value || null)}
                          MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
                        >
                          <MenuItem value={""}><em>Default Model</em></MenuItem>
                          {voiceModels?.map(model => <MenuItem key={model.id} value={model.id}>{model.name || model.modelId}</MenuItem>)}
                        </Select>
                      </Box>
                      <Box sx={{flex: 1}}>
                        <FieldLabel>Voice</FieldLabel>
                        <Autocomplete
                          freeSolo
                          options={GEMINI_VOICE_NAMES}
                          inputValue={voice.voice || ""}
                          onInputChange={(e, value) => update("voiceConfiguration", "voice", value)}
                          renderInput={(props) => <FlatAutocompleteInput {...props}/>}
                        />
                      </Box>
                    </Stack>
                    <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
                      <Box sx={{flex: 1}}>
                        <FlatField
                          label={"Voice Language Code"} value={voice.languageCode || ""}
                          onChange={e => update("voiceConfiguration", "languageCode", e.target.value)}
                          helper={`Defaults to the language code (${language?.code || "-"})`}
                        />
                      </Box>
                      <Box sx={{flex: 1}}>
                        <FieldLabel>Audio Encoding</FieldLabel>
                        <Select
                          value={voice.audioEncoding || "LINEAR16"} sx={selectSx}
                          onChange={e => update("voiceConfiguration", "audioEncoding", e.target.value)}
                          MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
                        >
                          {AUDIO_ENCODINGS.map(encoding => <MenuItem key={encoding} value={encoding}>{encoding}</MenuItem>)}
                        </Select>
                      </Box>
                    </Stack>
                    <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
                      <Box sx={{flex: 1}}>
                        <FlatField
                          label={"Pitch"} type={"number"} helper={"-20.0 to 20.0"}
                          inputProps={{step: 0.1, min: -20, max: 20}} value={voice.pitch ?? ""}
                          onChange={e => update("voiceConfiguration", "pitch", toNumber(e.target.value))}
                        />
                      </Box>
                      <Box sx={{flex: 1}}>
                        <FlatField
                          label={"Speaking Rate"} type={"number"} helper={"0.25 to 2.0"}
                          inputProps={{step: 0.05, min: 0.25, max: 2}} value={voice.speakingRate ?? ""}
                          onChange={e => update("voiceConfiguration", "speakingRate", toNumber(e.target.value))}
                        />
                      </Box>
                    </Stack>
                    <FlatField
                      label={"Style Prompt"} placeholder={"e.g. Speak in a calm, friendly tone."}
                      helper={"Optional delivery guidance, supports tags like [whispering], [laughing]"}
                      value={config.prompt || ""} multiline minRows={2}
                      onChange={e => onChange(languageId, {...config, prompt: e.target.value})}
                    />
                  </Stack>
                </Box>

                <Box>
                  <SubHeader icon={Mic01Icon} title={"Transcribe Configuration"}/>
                  <Stack direction={{xs: "column", sm: "row"}} gap={1.5}>
                    <Box sx={{flex: 1}}>
                      <FieldLabel>Transcribe Model</FieldLabel>
                      <Select
                        value={transcribe.transcribeModelId ?? ""} displayEmpty sx={selectSx}
                        onChange={e => update("transcribeConfiguration", "transcribeModelId", e.target.value || null)}
                        MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
                      >
                        <MenuItem value={""}><em>Default Model</em></MenuItem>
                        {languageModels.map(model => <MenuItem key={model.id} value={model.id}>{model.name || model.modelId}</MenuItem>)}
                      </Select>
                      {languageModels.length === 0 && (
                        <FieldHelper>No transcribe models available for {language?.language || "this language"}</FieldHelper>
                      )}
                    </Box>
                    <Box sx={{flex: 1}}>
                      <FlatField
                        label={"Transcribe Language Code"} value={transcribe.languageCode || ""}
                        onChange={e => update("transcribeConfiguration", "languageCode", e.target.value)}
                        helper={`Defaults to the language code (${language?.code || "-"})`}
                      />
                    </Box>
                  </Stack>
                </Box>
                {renderAfter && <Box sx={{mt: 3}}>{renderAfter(languageId, config)}</Box>}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Stack>
  );
};
